import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Box,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

interface ControlsProps {
  data: {
    lowLevel: {
      remoteStopStart: string;
      freeRunSignal: string;
      faultSignal: string;
    };
    highLevel: {
      modbus: string;
      bacnet: string;
      mqtt: string;
    };
  };
  onChange: (subSection: string, field: string, value: string) => void;
}

const ControlsSection: React.FC<ControlsProps> = ({ data, onChange }) => {
  const yesNoOptions = ['Y', 'N'];

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h5">6. Controls</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2">
            Enter "Y" for interface options which are available.
          </Typography>
        </Box>

        {/* Low Level Interface */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Low Level Interface</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth margin="normal" className="blue-highlight">
                <InputLabel id="remote-stop-start-label">0 V Remote Stop/Start</InputLabel>
                <Select
                  labelId="remote-stop-start-label"
                  value={data.lowLevel.remoteStopStart}
                  label="0 V Remote Stop/Start"
                  onChange={(e) => onChange('lowLevel', 'remoteStopStart', e.target.value)}
                  size="small"
                >
                  <MenuItem value="">
                    <em>Select</em>
                  </MenuItem>
                  {yesNoOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth margin="normal" className="blue-highlight">
                <InputLabel id="free-run-signal-label">0 V Free Run Signal</InputLabel>
                <Select
                  labelId="free-run-signal-label"
                  value={data.lowLevel.freeRunSignal}
                  label="0 V Free Run Signal"
                  onChange={(e) => onChange('lowLevel', 'freeRunSignal', e.target.value)}
                  size="small"
                >
                  <MenuItem value="">
                    <em>Select</em>
                  </MenuItem>
                  {yesNoOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth margin="normal" className="blue-highlight">
                <InputLabel id="fault-signal-label">0 V Fault Signal</InputLabel>
                <Select
                  labelId="fault-signal-label"
                  value={data.lowLevel.faultSignal}
                  label="0 V Fault Signal"
                  onChange={(e) => onChange('lowLevel', 'faultSignal', e.target.value)}
                  size="small"
                >
                  <MenuItem value="">
                    <em>Select</em>
                  </MenuItem>
                  {yesNoOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {/* High Level Interface */}
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>High Level Interface</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth margin="normal" className="blue-highlight">
                <InputLabel id="modbus-label">Modbus</InputLabel>
                <Select
                  labelId="modbus-label"
                  value={data.highLevel.modbus}
                  label="Modbus"
                  onChange={(e) => onChange('highLevel', 'modbus', e.target.value)}
                  size="small"
                >
                  <MenuItem value="">
                    <em>Select</em>
                  </MenuItem>
                  {yesNoOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth margin="normal" className="blue-highlight">
                <InputLabel id="bacnet-label">BACnet</InputLabel>
                <Select
                  labelId="bacnet-label"
                  value={data.highLevel.bacnet}
                  label="BACnet"
                  onChange={(e) => onChange('highLevel', 'bacnet', e.target.value)}
                  size="small"
                >
                  <MenuItem value="">
                    <em>Select</em>
                  </MenuItem>
                  {yesNoOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth margin="normal" className="blue-highlight">
                <InputLabel id="mqtt-label">MQTT</InputLabel>
                <Select
                  labelId="mqtt-label"
                  value={data.highLevel.mqtt}
                  label="MQTT"
                  onChange={(e) => onChange('highLevel', 'mqtt', e.target.value)}
                  size="small"
                >
                  <MenuItem value="">
                    <em>Select</em>
                  </MenuItem>
                  {yesNoOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>
      </AccordionDetails>
    </Accordion>
  );
};

export default ControlsSection;